import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum POv4LineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // SKU / ID of the item
  skuDescription = 'skuDescription', // descriptive name of the item (renamed from 'name')
  details = 'details', // verbose description of the item, optional (renamed from 'description')
  vendorSku = 'vendorSku', // vendor's SKU identifier, optional
  vendorSkuDescription = 'vendorSkuDescription', // vendor's SKU description, optional
  uom = 'uom', // unit of measure
  itemQty = 'itemQty', // quantity of the item for order
  unitPrice = 'unitPrice', // price per unit
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const POv4addressInfoSchema = yup.object().shape({
  attn: yup.string().nullable().optional(),
  companyName: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  zipCode: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  phoneNumber: yup.string().nullable().optional(),
  emailAddress: yup.string().nullable().optional(),
  displayMultipleLines: yup.string().nullable().optional(),
  combineAddressLines: yup.string().nullable().optional(),
})
export type POv4FileAddressData = yup.InferType<typeof POv4addressInfoSchema>

const miscChargeSchema = yup.object().shape({
  label: yup.string().nullable().optional(), // e.g. "Tax", "Shipping", "Freight"
  value: yup.string().nullable().optional(), // dollar amount
  percentage: yup.string().nullable().optional(), // e.g. "5" → renders "Tax (5%)"
})

const lineItemSchema = yup.object().shape({
  itemNumber: yup.number().nullable().optional(),
  itemName: yup.string().nullable().optional(),
  skuDescription: yup.string().nullable().optional(),
  details: yup.string().nullable().optional(),
  vendorSku: yup.string().nullable().optional(),
  vendorSkuDescription: yup.string().nullable().optional(),
  uom: yup.string().nullable().optional(),
  itemQty: yup.string().nullable().optional(),
  unitPrice: yup.string().nullable().optional(),
  lineTotal: yup.string().nullable().optional(),
})

const POv4itemHeadersSchema = yup.object().shape({
  itemNumber: yup.string().nullable().optional(),
  itemName: yup.string().nullable().optional(),
  skuDescription: yup.string().nullable().optional(),
  details: yup.string().nullable().optional(),
  vendorSku: yup.string().nullable().optional(),
  vendorSkuDescription: yup.string().nullable().optional(),
  uom: yup.string().nullable().optional(),
  itemQty: yup.string().nullable().optional(),
  unitPrice: yup.string().nullable().optional(),
  lineTotal: yup.string().nullable().optional(),
})

export const POv4FileDataSchema = yup.object().shape({
  issuer: POv4addressInfoSchema,
  orderNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  destination: POv4addressInfoSchema,
  vendor: POv4addressInfoSchema,
  itemHeaders: POv4itemHeadersSchema,
  items: yup.array().of(lineItemSchema).required(),
  items2: yup.array().of(lineItemSchema).optional(),
  dontRoundUnitPrice: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  useLocaleQuantityFormatting: yup.string().nullable().optional(),
  termsAndConditionsUrl: yup.string().nullable().optional(),
  termsAndConditionsLabel: yup.string().nullable().optional(),
  miscCharges: yup.array().of(miscChargeSchema).optional(),
  currency: yup.string().nullable().optional(),
})

export type POv4FileData = yup.InferType<typeof POv4FileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<POv4FileData>

export const POv4FileMappings: RequiredFileData = {
  issuer: {
    attn: '0486bc14-772a-4c35-afc2-879a9b475374',
    companyName: '1a7ab665-942a-4d85-b403-b198df457527',
    addressLine1: '3b9e755c-2ad8-4825-864f-61abd61c2da1',
    addressLine2: '506a4c00-1af7-4fac-90e1-b1821e65cd56',
    city: 'ccd2e98c-abe7-4ae5-9acc-a6a66eb4e3ed',
    state: 'f1ad53c0-640e-4e7f-8db3-9cff2279a313',
    zipCode: '860e8552-bc01-4b4a-be09-12a0730ac427',
    country: '389b6e0d-405e-40bf-a60f-ad191a34e823',
    phoneNumber: 'cdfc1f21-f1d5-4020-9a8f-f10a07fb0921',
    emailAddress: 'fcc6c402-a848-4623-aa6e-10db7d6137ec',
    displayMultipleLines: '9d5e6557-c141-4da6-bcc4-dff6c89cc207',
    combineAddressLines: '5a2d9c4e-1b7f-4e36-8c91-2f6a8d3b7c10',
  },
  orderNumber: '2a825570-10e1-4302-9000-d7eeed733173',
  paymentTerms: '5b010e23-b72d-4702-b22b-fb4ae0021df4',
  freightTerms: '39d42110-93fe-4cc9-932e-e686f596b7fb',
  dateIssued: '893f79b3-6b31-4a63-b369-8ccd662b225b',
  dateReady: 'c2997440-d503-49cb-a3a7-bc834ec7a7bd',
  destination: {
    attn: '512426e7-ed4c-44eb-902a-9301a8a578b7',
    companyName: 'b81ebd64-7101-4d22-8791-dec3c9b10571',
    addressLine1: '6fbc618f-12d4-4fb7-bdae-adeb70ad5b30',
    addressLine2: '8d931518-6f94-43aa-8adb-8c64c7842613',
    city: 'f25eb9f3-f35e-4aec-aa1c-93857401e278',
    state: '35f99e5a-6f4e-4524-9285-6c99f182dea1',
    zipCode: '54e3ab32-404b-462d-84c2-b8f10cb2812e',
    country: '7f8d4fc3-df20-4b16-b6fc-a43337b08e17',
    phoneNumber: '01dbc3a2-db66-4ba6-8ff6-cc2d5d5de976',
    emailAddress: 'e8113321-d9f9-4b85-8d73-3dadf4caa417',
    displayMultipleLines: 'ea1ebdda-1566-43e2-b8b1-af0ac4eca529',
    combineAddressLines: 'c8f1a7b2-6e3d-4a95-9b24-5d7e2c1f0a68',
  },
  vendor: {
    attn: '401bcff7-8fbb-48bc-b0de-d87d13de71ed',
    companyName: '4380791e-2c8b-40f4-a182-ee9e9dbee772',
    addressLine1: 'bbf6dd5f-6de8-49dd-b7d9-5a5cd070ef4b',
    addressLine2: '910313d8-7670-4c25-8c53-84e8b81cb427',
    city: '26d64903-dde6-4cac-bc02-1e74a4c4efaa',
    state: 'a09d37d6-086a-4fb7-be9b-bf4d5add5190',
    zipCode: '4bd04322-0982-438c-87a4-478e3cd44e8a',
    country: '06f9b33d-dfa1-4439-b818-3aaa116276e9',
    phoneNumber: 'ad37e8fc-c073-4330-a0c3-0fb74511fab6',
    emailAddress: '2b824e1f-735a-4d5b-9752-ec9b325d914d',
    displayMultipleLines: '3ef20019-f3fc-4b0a-afc8-cb1339a10759',
    combineAddressLines: '1e7c3d5a-9b42-4f6e-8a13-7c2d5b9f6a04',
  },
  itemHeaders: {
    itemNumber: 'ed5eb29d-b0d5-4b51-9025-0107f1b757f2',
    itemName: 'df9be919-42a1-45b6-934a-4d363e1efe07',
    skuDescription: '5fe0ff95-7f2b-415a-8d4e-b07e5cd9a4ea',
    details: '6bcf3183-5272-4e0d-9752-91769298b637',
    vendorSku: 'a2d6f33f-4aff-44cc-944f-9461a166ffa3',
    vendorSkuDescription: '0a539a02-67aa-46f2-9831-dd17abd17591',
    uom: 'ac5c6784-73b6-4dc0-9b2a-cf4b92ca4a88',
    itemQty: '76585e9a-d07e-4c53-ad64-29e5c052aafe',
    unitPrice: 'bf1e024c-757e-4f18-a075-a06bd49faf3d',
    lineTotal: '9c713ed2-8992-4ffc-8d3a-004beea7fb87',
  },
  items: [
    {
      itemNumber: null,
      itemName: 'c5ce87b5-3369-44d4-88a4-60968ca717ab',
      skuDescription: '2e31efe9-8e6f-48be-bd52-2faf8950eabd',
      details: 'ef1b39f6-b478-482c-a545-c245f5974897',
      vendorSku: 'a139f223-ff9c-4d98-84e8-2a2268b1175e',
      vendorSkuDescription: '9065dbd1-ab31-4e93-883e-1f7b84ae027e',
      uom: '2e0ad7e5-f816-4ef2-9e07-9fd8195e63c9',
      itemQty: '1abb65d7-3887-4088-95c5-78d444096bb3',
      unitPrice: 'e5e319fa-4fa7-4a37-9692-ea763201a034',
      lineTotal: '28c87121-7d63-44ce-abec-276724007827',
    },
  ],
  items2: [
    {
      itemNumber: null,
      itemName: 'd2da46a2-e00a-4b3f-82aa-13e784c47ed7',
      skuDescription: '971dd6fb-133d-464f-a69d-14b443ee5dfb',
      details: '8e02dfdb-25b6-47b8-aefd-50d4b589efb9',
      vendorSku: '96de0cd6-f459-4747-8d90-03d6ba7f6c06',
      vendorSkuDescription: 'e1497f15-8539-44c3-99b7-a668d3a02f09',
      uom: '02ce3655-07bc-4f1c-ac70-5c5c4f324cd8',
      itemQty: 'b7890083-d310-488f-8d93-97b906d53eca',
      unitPrice: '5a3bd73b-a4b8-4dc9-9795-a61d9bc515ed',
      lineTotal: '067a9050-9ea5-4580-8150-6a76c8ed722f',
    },
  ],
  dontRoundUnitPrice: 'eb4b4972-a40b-4e99-9d17-91e630598e33',
  agreement: '70472bf8-4d7c-487e-a317-e8f0d9c33dd6',
  instructions: 'daa46f7e-83a6-4e92-993f-5da0d1a59b52',
  useLocaleQuantityFormatting: '236cce13-0215-494d-bcb5-e6503ba15dd8',
  termsAndConditionsUrl: 'e80d8aee-0272-4a1d-86a0-3f838531c69c',
  termsAndConditionsLabel: '391ae1a5-5b73-4ec7-8bba-eed8e5066f90',
  miscCharges: [
    {
      label: 'b12f9f56-fc3d-427c-b03e-413cdbbe77c2',
      value: '104a027c-ef5e-416e-9b83-9acd7b3073fe',
      percentage: '1b7e38d5-7fa4-45bb-9bbb-b55214895ef4',
    },
  ],
  currency: 'bfabe13f-895b-48ac-b6ca-da763b0e75eb',
}
